<script setup lang="ts">
import { ref } from 'vue';

import AttachmentPreview from '@satellite/components/elements/attachments/AttachmentPreview.vue';
import AttachmentThumbnail from '@satellite/components/elements/attachments/AttachmentThumbnail.vue';

import type { Maybe, YardTaskAttachment } from '@nova/core';

defineOptions({ name: 'AttachmentList' });

const {
  attachments,
  selectable = true,
  removable = false,
  isRemoving = false,
  removeLabel = '',
  testId = 'attachment'
} = defineProps<{
  /** The documents to show. Ordering is the caller's. */
  attachments: YardTaskAttachment[];
  /**
   * Whether an attachment can be opened at all. Off, the tiles still show the documents but
   * nothing opens — for callers that may list attachments without being allowed to read them.
   */
  selectable?: boolean;
  /** Renders the remove affordance on every tile; the list only reports it through `remove`. */
  removable?: boolean;
  /** Blocks the remove affordance while a removal is already in flight. */
  isRemoving?: boolean;
  /** Accessible name for the remove affordance. */
  removeLabel?: string;
  /** Prefix for every `data-testid` below, e.g. `<testId>-list`. */
  testId?: string;
}>();

const emit = defineEmits<{
  remove: [attachment: YardTaskAttachment];
  'error:thumbnail': [attachment: YardTaskAttachment];
  /** An attachment this app cannot preview can be neither opened nor saved on this device. */
  unsupported: [];
  /** An attachment this app cannot preview failed to save. */
  'error:download': [error: unknown];
}>();

/**
 * Opening an attachment is this list's own business: the caller supplies the documents and
 * hears about writes, and never has to know which of them this app can render itself.
 */
const previewAttachment = ref<Maybe<YardTaskAttachment>>(null);
</script>

<template>
  <div class="d-flex flex-row flex-wrap gap-2 align-start" :data-testid="`${testId}-list`">
    <attachment-thumbnail
      v-for="attachment in attachments"
      :key="attachment.id"
      :attachment="attachment"
      :selectable="selectable"
      :removable="removable"
      :is-removing="isRemoving"
      :remove-label="removeLabel"
      :test-id="testId"
      @select="previewAttachment = attachment"
      @remove="emit('remove', attachment)"
      @error:thumbnail="emit('error:thumbnail', attachment)" />

    <!-- @slot Trailing tile alongside the attachments, e.g. an upload affordance. -->
    <slot name="append" />
  </div>

  <attachment-preview
    v-model="previewAttachment"
    :test-id="testId"
    @unsupported="emit('unsupported')"
    @error:download="emit('error:download', $event)">
    <template #pdf-error>
      <!-- @slot Shown when a PDF cannot be read. -->
      <slot name="preview-error" />
    </template>
  </attachment-preview>
</template>
